/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { BiSortAlt2 } from 'react-icons/bi';
import { Container, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowRight,
  FiEdit2,
  // FiShare2,
  FiFilter,
  FiArrowLeft,
  FiFileText,
  FiShare2,
} from 'react-icons/fi';
import VisaSearchForm from '../../components/VisaSearchForm/VisaSearchForm';
import VisaCard from '../../components/VisaCard/VisaCard';
import VisaFilters from '../../components/VisaFilters/VisaFilters';
import FareSummary from '../../components/FareSummary/FareSummary';
import RequiredDocuments from '../../components/RequiredDocuments/RequiredDocuments';
import VisaDetailsModal from '../../components/VisaDetailsModal/VisaDetailsModal';
import {
  extractVisaSearchResult,
  searchOnlineVisaApi,
  fetchVisaDetailsApi,
  getRequiredDocumentsApi,
  extractVisaContext,
} from '../../api/onlineVisaApi';
import useCountries from '../../hooks/useCountries';
import {
  buildSearchQuery,
  parseSearchQuery,
  formatEntry,
  formatVisaPurpose,
  formatPrice,
} from '../../utils/onlineVisaUtils';
import { ONLINE_VISA_ROUTES } from '../../constants/onlineVisaConstants';
import './OnlineVisaOptionsPage.scss';
import { isNullOrEmpty } from '../../../../shared/helper-utils/appUtils';
import AppAlert from '../../../../shared/components/app-alert/AppAlert';
import { checkValidMobileNumber } from '../../../../shared/constants/Constants';
import AppButton from '../../../../shared/components/app-button/AppButton';
import AppNumberInput from '../../../../shared/components/app-number-input/AppNumberInput';
import AppModal from '../../../../shared/components/app-modal/AppModal';

const DEFAULT_FILTERS = { visaTypes: [], entries: [], sortBy: 'price-asc' };

const SORT_TABS = [
  {
    key: 'price',
    label: 'Price Low to High',
    descLabel: 'Price High to Low',
    defaultSortBy: 'price-asc',
  },
  {
    key: 'processing',
    label: 'Fastest Processing',
    descLabel: 'Slowest Processing',
    defaultSortBy: 'processing-asc',
  },
  {
    key: 'stay',
    label: 'Longest Stay',
    descLabel: 'Shortest Stay',
    defaultSortBy: 'stay-desc',
  },
];

function titleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const PAX_LABELS = {
  ADT: 'Adult',
  CHD: 'Child',
  INF: 'Infant',
};

const getVisaKey = (visa) => visa?.cacheKey || visa?.bookingKey || visa?.visaId;

const getFareNumber = (...values) =>
  values.reduce((total, value) => total + (Number(value) || 0), 0);

const formatMessageAmount = (amount, currency) =>
  formatPrice(amount, currency) ||
  `${currency || 'INR'} ${Math.round(amount || 0)}`;

const buildVisaWhatsappMessage = (selectedVisas) => {
  let msg = '*Visa Quotation*\n\n';

  selectedVisas.forEach((visa, index) => {
    const currency = visa?.visaFare?.currency || 'INR';
    const validity =
      visa?.validityDisplay ||
      `${visa?.validity || ''} ${visa?.validityType || ''}`.trim();
    const stay =
      visa?.durationDisplay ||
      `${visa?.duration || ''} ${visa?.durationType || ''}`.trim();
    const title =
      visa?.visaName ||
      `${visa?.destination?.destinationName || ''} ${stay}`.trim();
    const fareRows = Array.isArray(visa?.visaFare?.paxWisePriceList)
      ? visa.visaFare.paxWisePriceList
      : [];

    msg += `${index + 1}. *${title}*\n`;
    msg += `   Type: ${visa?.visaType || '-'} | Entry: ${visa?.entry || '-'}\n`;
    msg += `   Validity: ${validity || '-'} | Stay: ${stay || '-'}\n`;
    msg += '   *Fare Breakdown:*\n';

    fareRows.forEach((fare) => {
      const paxType = fare?.paxType || '';
      const paxLabel = PAX_LABELS[paxType] || paxType || 'Traveller';
      const count = Number(fare?.paxCount) || 0;
      if (count <= 0) return;

      const embassy = getFareNumber(fare?.paxTotalAmount, fare?.PaxBaseFare);
      const platform = getFareNumber(
        fare?.PaxHiddenMarkupOnBaseFare,
        fare?.hiddenMarkup,
      );
      const tax = getFareNumber(fare?.PaxHandleCharges, fare?.PaxTfeeInv);
      const total = Number(fare?.PaxFareQuoteTotal) || 0;

      msg += `     \u2022 ${paxLabel} x${count}: ${formatMessageAmount(
        total,
        currency,
      )}\n`;
      msg += `       Embassy: ${formatMessageAmount(
        embassy,
        currency,
      )} | Platform: ${formatMessageAmount(
        platform,
        currency,
      )} | Tax: ${formatMessageAmount(tax, currency)}\n`;
    });

    const totalAmount =
      Number(visa?.visaFare?.fareQuoteTotal) ||
      Number(visa?.visaFare?.totalAmount) ||
      fareRows.reduce(
        (sum, fare) => sum + (Number(fare?.PaxFareQuoteTotal) || 0),
        0,
      );
    msg += `   *Total: ${formatMessageAmount(totalAmount, currency)}*\n\n`;
  });

  return msg.trim();
};

function OnlineVisaOptionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = useMemo(
    () => parseSearchQuery(searchParams),
    [searchParams],
  );
  const { countries } = useCountries();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visaList, setVisaList] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedVisaId, setSelectedVisaId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [shareSelectionMode, setShareSelectionMode] = useState(false);
  const [selectedShareVisaIds, setSelectedShareVisaIds] = useState([]);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappValidationError, setWhatsappValidationError] = useState(false);
  const [shareAlert, setShareAlert] = useState(undefined);
  const [showModify, setShowModify] = useState(false);

  // Required documents (fetched per selected visa)
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Visa details drawer
  const [showVisaDetails, setShowVisaDetails] = useState(false);
  const [visaDetail, setVisaDetail] = useState(null);
  const [visaDetailLoading, setVisaDetailLoading] = useState(false);
  const [visaDetailError, setVisaDetailError] = useState('');
  const [traceId, setTraceId] = useState('');
  const destination = useMemo(() => {
    const match = countries.find(
      (c) => c.countryCode === initialSearch.destinationCode,
    );
    return {
      destinationCode: initialSearch.destinationCode,
      destinationName:
        initialSearch.destinationName ||
        (match && match.countryName ? match.countryName.toUpperCase() : ''),
    };
  }, [countries, initialSearch]);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      if (!initialSearch.destinationCode || !initialSearch.nationality) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await searchOnlineVisaApi({
          adults: initialSearch.adults,
          children: initialSearch.children,
          infants: initialSearch.infants,
          nationality: initialSearch.nationality,
          destination: {
            destinationCode: destination.destinationCode,
            destinationName: destination.destinationName,
          },
          visaTypes: isNullOrEmpty(initialSearch.visaPurpose)
            ? []
            : [initialSearch.visaPurpose],
          childAges: initialSearch.childAges,
          infantAges: initialSearch.infantAges,
        });
        if (cancelled) return;
        setTraceId(response?.ApiStatus?.Result?.traceId || '');
        const parsed = extractVisaSearchResult(response);
        setVisaList(parsed.visaList || []);
        setSelectedVisaId(
          parsed.visaList && parsed.visaList[0]
            ? parsed.visaList[0].cacheKey
            : null,
        );
      } catch (e) {
        if (!cancelled) {
          setError('Something went wrong while fetching visa options.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [
    initialSearch.destinationCode,
    initialSearch.nationality,
    initialSearch.adults,
    initialSearch.children,
    initialSearch.childAges,
    initialSearch.infantAges,
    initialSearch.infants,
    destination.destinationName,
  ]);

  const filteredVisas = useMemo(() => {
    let list = [...visaList];

    if (filters.visaTypes && filters.visaTypes.length > 0) {
      list = list.filter((v) => filters.visaTypes.includes(v.visaType));
    }
    if (filters.entries && filters.entries.length > 0) {
      list = list.filter((v) => filters.entries.includes(v.entry));
    }

    switch (filters.sortBy) {
      case 'price-desc':
        list.sort(
          (a, b) =>
            (b?.visaFare?.totalAmount || 0) - (a?.visaFare?.totalAmount || 0),
        );
        break;
      case 'processing-asc':
        list.sort((a, b) => {
          const aDays = a?.processingTime?.replace(/[^0-9-]/g, '') || 0;
          const bDays = b?.processingTime?.replace(/[^0-9-]/g, '') || 0;
          return parseInt(aDays, 10) - parseInt(bDays, 10);
        });
        break;
      case 'processing-desc':
        list.sort((a, b) => {
          const aDays = a?.processingTime?.replace(/[^0-9-]/g, '') || 0;
          const bDays = b?.processingTime?.replace(/[^0-9-]/g, '') || 0;
          return parseInt(bDays, 10) - parseInt(aDays, 10);
        });
        break;
      case 'stay-desc':
        list.sort((a, b) => (b?.duration || 0) - (a?.duration || 0));
        break;
      case 'stay-asc':
        list.sort((a, b) => (a?.duration || 0) - (b?.duration || 0));
        break;
      case 'price-asc':
      default:
        list.sort(
          (a, b) =>
            (a?.visaFare?.totalAmount || 0) - (b?.visaFare?.totalAmount || 0),
        );
        break;
    }

    return list;
  }, [visaList, filters]);

  const selectedVisa = useMemo(() => {
    // if (!selectedVisaId) return filteredVisas[0] || null;
    return (
      filteredVisas.find((v) => (v.visaId || v.cacheKey) === selectedVisaId) ||
      filteredVisas[0] ||
      null
    );
  }, [filteredVisas, selectedVisaId]);

  const handleModify = (nextForm) => {
    const qs = buildSearchQuery(nextForm);
    navigate(`${ONLINE_VISA_ROUTES.OPTIONS}?${qs}`);
    setShowModify(false);
  };

  const handleSelectVisa = (visa) => {
    setSelectedVisaId(visa?.visaId || visa?.cacheKey || null);
  };

  const selectedShareVisas = useMemo(() => {
    return visaList.filter((visa) =>
      selectedShareVisaIds.includes(getVisaKey(visa)),
    );
  }, [selectedShareVisaIds, visaList]);

  const handleShareClick = () => {
    if (!shareSelectionMode) {
      setShareSelectionMode(true);
      setSelectedShareVisaIds([]);
      return;
    }

    if (selectedShareVisaIds.length === 0) {
      setShareAlert({
        title: 'WhatsApp',
        description: 'Select atleast one Visa Type to share the quotation.',
        variant: 'error',
      });
      return;
    }

    setShowWhatsappModal(true);
  };

  const handleToggleShareVisa = (visa) => {
    const key = getVisaKey(visa);
    if (!key) return;
    setSelectedShareVisaIds((prev) => {
      if (prev.includes(key)) {
        return prev.filter((id) => id !== key);
      }
      return [...prev, key];
    });
  };

  const handleCloseWhatsappModal = () => {
    setShowWhatsappModal(false);
    setWhatsappNumber('');
    setWhatsappValidationError(false);
  };

  const handleSendWhatsappQuotation = () => {
    if (
      whatsappNumber === '' ||
      whatsappNumber === undefined ||
      checkValidMobileNumber(whatsappNumber)
    ) {
      setWhatsappValidationError(true);
      return;
    }

    const msg = buildVisaWhatsappMessage(selectedShareVisas);
    const encodedMessage = encodeURIComponent(msg);
    const whatsAppUrl = `https://api.whatsapp.com/send?phone=91${whatsappNumber}&text=${encodedMessage}`;
    window.open(whatsAppUrl, '_blank');
    handleCloseWhatsappModal();
    setShareSelectionMode(false);
    setSelectedShareVisaIds([]);
  };

  const handleSortChange = (sortKey) => {
    setFilters((prev) => {
      const currentSortBy = prev.sortBy || 'price-asc';
      const base = sortKey;
      let nextSortBy = SORT_TABS.find((t) => t.key === base)?.defaultSortBy;

      if (currentSortBy.startsWith(base)) {
        nextSortBy = currentSortBy.endsWith('-asc')
          ? `${base}-desc`
          : `${base}-asc`;
      }

      return { ...prev, sortBy: nextSortBy };
    });
  };

  const handleProceed = () => {
    if (!selectedVisa) return;
    const qs = buildSearchQuery({
      ...initialSearch,
      destinationName: destination.destinationName,
    });
    navigate(`${ONLINE_VISA_ROUTES.BOOKING}?${qs}`, {
      state: {
        visa: selectedVisa,
        search: {
          ...initialSearch,
          destinationName: destination.destinationName,
        },
        traceId,
      },
    });
  };

  // Fetch required documents whenever the selected visa changes.
  useEffect(() => {
    let cancelled = false;
    if (!selectedVisa) {
      setDocuments([]);
      return () => {};
    }
    const ctx = extractVisaContext(selectedVisa, traceId);
    if (!ctx.cacheKey && !ctx.bookingKey) {
      setDocuments([]);
      return () => {};
    }
    setDocumentsLoading(true);
    (async () => {
      const docs = await getRequiredDocumentsApi(ctx);
      if (cancelled) return;
      setDocuments(docs || []);
      setDocumentsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedVisa]);

  const handleShowVisaDetails = async () => {
    if (!selectedVisa) return;
    setShowVisaDetails(true);
    setVisaDetailLoading(true);
    setVisaDetailError('');
    setVisaDetail(null);
    try {
      const ctx = extractVisaContext(selectedVisa, traceId);
      const detail = await fetchVisaDetailsApi(ctx);
      if (detail) {
        setVisaDetail(detail);
      } else {
        setVisaDetailError('Unable to load visa details. Please try again.');
      }
    } catch (e) {
      setVisaDetailError('Something went wrong while fetching visa details.');
    } finally {
      setVisaDetailLoading(false);
    }
  };

  const handleCloseVisaDetails = () => {
    setShowVisaDetails(false);
  };

  const citizenCode = initialSearch.nationality || '—';
  const citizenCountry = useMemo(() => {
    const match = countries.find((c) => c.countryCode === citizenCode);
    return match ? titleCase(match.countryName) : '';
  }, [countries, citizenCode]);

  const paxCount = {
    adults: initialSearch.adults,
    children: initialSearch.children,
    infants: initialSearch.infants,
  };
  const totalPax =
    (paxCount.adults || 0) + (paxCount.children || 0) + (paxCount.infants || 0);

  return (
    <main className="online-visa-options">
      {shareAlert && (
        <AppAlert
          showIcon
          variant={shareAlert.variant}
          title={shareAlert.title}
          description={shareAlert.description}
          onHide={() => {
            setShareAlert(undefined);
          }}
        />
      )}
      <Container>
        {/* BLACK SUMMARY BAR */}
        <section className="visa-summary-bar">
          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">VISA FOR CITIZEN OF</span>
            <strong
              className="visa-summary-bar__value cursor-pointer"
              onClick={() => setShowModify(true)}
            >
              {citizenCode}
            </strong>
            <span className="visa-summary-bar__sub">{citizenCountry}</span>
          </div>

          <button
            type="button"
            className="visa-summary-bar__arrow"
            aria-label="Visa flow"
            tabIndex={-1}
          >
            <FiArrowRight />
          </button>

          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">DESTINATION COUNTRY</span>
            <strong
              className="visa-summary-bar__value cursor-pointer"
              onClick={() => setShowModify(true)}
            >
              {destination.destinationCode || '—'}
            </strong>
            <span className="visa-summary-bar__sub">
              {titleCase(destination.destinationName)}
            </span>
          </div>

          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">VISA TYPE</span>
            <strong
              className="visa-summary-bar__value cursor-pointer"
              onClick={() => setShowModify(true)}
            >
              {formatVisaPurpose(initialSearch.visaPurpose).toUpperCase()}
            </strong>
          </div>

          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">TRAVELLERS</span>
            <strong
              className="visa-summary-bar__value cursor-pointer"
              onClick={() => setShowModify(true)}
            >
              {totalPax} {totalPax === 1 ? 'Traveller' : 'Travellers'}
            </strong>
          </div>

          <button
            type="button"
            className="visa-summary-bar__modify cursor-pointer"
            onClick={() => setShowModify(true)}
          >
            <span>Modify</span>
            <FiEdit2 />
          </button>
        </section>

        {/* MAIN LAYOUT */}
        <Row className="g-4 online-visa-options__main">
          {/* LEFT: results */}
          <Col lg={9} md={12}>
            <div className="online-visa-options__results-card">
              <div className="online-visa-options__sort-row">
                <span className="online-visa-options__sort-prefix">
                  <BiSortAlt2 style={{ fontSize: '18px' }} />
                  Sort By :
                </span>
                <div className="online-visa-options__sort-tabs">
                  {SORT_TABS.map((t) => {
                    const { key, label: baseLabel, descLabel } = t;
                    const isActive = filters.sortBy?.startsWith(key);
                    const label =
                      isActive && filters.sortBy?.endsWith('-desc')
                        ? descLabel
                        : baseLabel;
                    let tabClassName = 'online-visa-options__sort-tab';
                    if (isActive) {
                      tabClassName = `${tabClassName} online-visa-options__sort-tab--active`;
                    }
                    return (
                      <button
                        key={key}
                        type="button"
                        className={tabClassName}
                        onClick={() => handleSortChange(key)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="online-visa-options__filter-btn"
                  onClick={() => setShowFilters(true)}
                >
                  <FiFilter />
                  <span>Filter</span>
                </button>
              </div>

              {error && (
                <div className="online-visa-options__error">{error}</div>
              )}

              {loading && (
                <div className="online-visa-options__loader">
                  {[1, 2, 3].map((k) => (
                    <div key={k} className="online-visa-options__skeleton" />
                  ))}
                </div>
              )}

              {!loading && filteredVisas.length === 0 && !error && (
                <div className="online-visa-options__empty">
                  <h3>No visa options match your filters.</h3>
                  <p>
                    Try clearing filters or modifying your search using the
                    header above.
                  </p>
                </div>
              )}

              {!loading && filteredVisas.length > 0 && (
                <div className="online-visa-options__list">
                  {filteredVisas.map((visa) => {
                    const id = visa.cacheKey;
                    return (
                      <VisaCard
                        key={id}
                        visa={visa}
                        selected={id === selectedVisa?.cacheKey}
                        onSelect={handleSelectVisa}
                        shareSelectionMode={shareSelectionMode}
                        shareSelected={selectedShareVisaIds.includes(
                          getVisaKey(visa),
                        )}
                        onShareToggle={handleToggleShareVisa}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </Col>

          {/* RIGHT: sidebar */}
          {selectedVisa && (
            <Col lg={3} md={12}>
              <div className="online-visa-options__sidebar">
                <button
                  type="button"
                  className="online-visa-options__share-btn"
                  onClick={handleShareClick}
                >
                  <span>
                    {shareSelectionMode && selectedShareVisaIds.length > 0
                      ? `Share Selected (${selectedShareVisaIds.length})`
                      : 'Share Quotation'}
                  </span>
                  <FiShare2 />
                </button>
                <FareSummary
                  visa={selectedVisa}
                  paxCount={paxCount}
                  childAges={initialSearch.childAges}
                  infantAges={initialSearch.infantAges}
                />
                <RequiredDocuments
                  documents={documents}
                  loading={documentsLoading}
                />
              </div>
            </Col>
          )}
        </Row>
      </Container>

      {/* STICKY PROCEED BAR */}
      <div className="online-visa-options__proceed-bar">
        <Container className="online-visa-options__proceed-inner">
          <div className="online-visa-options__proceed-text">
            <h4 className="online-visa-options__proceed-title">
              {titleCase(destination.destinationName) || 'Destination'}{' '}
              {destination.destinationCode
                ? `(${destination.destinationCode})`
                : ''}
            </h4>
            <p className="online-visa-options__proceed-subtitle">
              {selectedVisa
                ? `${selectedVisa.durationDisplay || ''} ${formatEntry(
                    selectedVisa.entry,
                  )} Visa`.trim()
                : 'Select a visa'}{' '}
              | {totalPax} {totalPax === 1 ? 'Traveler' : 'Travellers'}
            </p>
          </div>
          <div className="online-visa-options__proceed-actions">
            <button
              type="button"
              className="online-visa-options__details-btn"
              disabled={!selectedVisa}
              onClick={handleShowVisaDetails}
            >
              <FiFileText />
              <span>View Details</span>
            </button>
            <button
              type="button"
              className="online-visa-options__proceed-btn"
              disabled={!selectedVisa}
              onClick={handleProceed}
            >
              <span>Proceed to Add Travellers</span>
              <FiArrowRight />
            </button>
          </div>
        </Container>
      </div>

      {/* VISA DETAILS DRAWER */}
      <VisaDetailsModal
        show={showVisaDetails}
        onHide={handleCloseVisaDetails}
        visaDetail={visaDetail}
        loading={visaDetailLoading}
        error={visaDetailError}
      />

      {/* FILTERS DRAWER */}
      <Modal
        show={showFilters}
        onHide={() => setShowFilters(false)}
        centered
        dialogClassName="online-visa-options__filter-modal"
      >
        <Modal.Body>
          <VisaFilters
            visaList={visaList}
            filters={filters}
            onFilterChange={(next) => {
              setFilters(next);
              setShowFilters(false);
            }}
          />
        </Modal.Body>
      </Modal>

      {showWhatsappModal && (
        <AppModal
          onModalClose={handleCloseWhatsappModal}
          modalTitle="Share Quotation via WhatsApp"
          showCloseButton
          dialogClassName="online-visa-options__whatsapp-modal"
        >
          <div className="online-visa-options__whatsapp-content">
            <div className="online-visa-options__whatsapp-list">
              {selectedShareVisas.map((visa) => (
                <div
                  key={getVisaKey(visa)}
                  className="online-visa-options__whatsapp-item"
                >
                  <div>
                    <strong>{visa?.visaName}</strong>
                    <span>
                      {visa?.visaType || '-'} | {visa?.entry || '-'} Entry |
                      Stay{' '}
                      {visa?.durationDisplay ||
                        `${visa?.duration || ''} ${
                          visa?.durationType || ''
                        }`.trim()}
                    </span>
                  </div>
                  <b>
                    {formatPrice(
                      visa?.visaFare?.fareQuoteTotal ||
                        visa?.visaFare?.totalAmount,
                      visa?.visaFare?.currency || 'INR',
                    )}
                  </b>
                </div>
              ))}
            </div>

            <AppNumberInput
              name="whatsappNumber"
              label="WhatsApp Number *"
              placeholderText="Enter 10 digit mobile number"
              value={whatsappNumber}
              onChange={(value) => {
                setWhatsappNumber(value);
                setWhatsappValidationError(false);
              }}
              errorText={
                whatsappValidationError && 'Please Enter Mobile Number'
              }
            />
            {checkValidMobileNumber(whatsappNumber) &&
              whatsappValidationError && (
                <small className="app-error-text">Enter Valid Number</small>
              )}

            <div className="online-visa-options__whatsapp-actions">
              <AppButton
                btnText="Cancel"
                variant="outlined"
                onClick={handleCloseWhatsappModal}
              />
              <AppButton
                btnText="Send via WhatsApp"
                variant="secondary"
                onClick={handleSendWhatsappQuotation}
              />
            </div>
          </div>
        </AppModal>
      )}

      {/* MODIFY SEARCH MODAL */}
      <Modal
        show={showModify}
        onHide={() => setShowModify(false)}
        centered
        dialogClassName="online-visa-options__modify-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Modify Search</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex align-items-center justify-content-center">
          <VisaSearchForm
            initialValues={initialSearch}
            onSearch={handleModify}
          />
        </Modal.Body>
      </Modal>

      <button
        type="button"
        className="online-visa-options__back-btn"
        onClick={() => navigate(ONLINE_VISA_ROUTES.LANDING)}
        aria-label="Back to search"
      >
        <FiArrowLeft />
      </button>
    </main>
  );
}

export default OnlineVisaOptionsPage;
